// CLARTEXT · /abo — Verkaufsseite nach den drei Mandantenentscheidungen.
// Route: /abo (öffentlich, ohne Layout). Ziel von Tür A der Abschlussseite.
//
// Stand B2-E2 (Betreiber-GO: 1 verbindliche Entlastungsbotschaften · 2a Sifa
// offen sichtbar · 3a Beweiskette · 4b Chris mit Hierarchie):
//   SCHUSS 1 „Brauche ich das?"
//     01 Hero + CHRIS-MOMENT 1 (Pose 21)
//     02 Drei Entlastungsbotschaften → „Mehr Zeit fürs Geschäft."
//     03 Sifa: Externe Fachleute dort, wo sie gebraucht werden (offen sichtbar)
//     Zäsur „Schön gesagt. Zeig mir das."
//   SCHUSS 2 „Beweis"
//     04 GBU „Ein Wort statt leerer Formulare" + CHRIS-MOMENT 2 (Pose 23)
//     05 Unterweisung + Mitarbeiter-Handy + Wiederholung (EINE Story, Chris 13)
//     06 Gefahrstoff → Verzeichnis → Haut-/Handschutz (Gamechanger, Chris 17)
//     07 Führerscheinkontrolle (Gamechanger, Chris 19)
//     08 Prüfmappe (Abschluss Schuss 2, Chris 10)
//   SCHUSS 3 „Geld / Test"
//     09 Preis + CHRIS-MOMENT 3 (Pose 22) + Trial-Matrix
//     10 Abschluss „Nicht glauben. Ausprobieren." (Pose 23)
//
// Fachliche SSoT: /app/memory/ABO_SEITE_B2D2_FINAL_CONSOLIDATED_SALES_
// ARCHITECTURE.md; Betreiberentscheidungen B2-E2 überschreiben verworfene
// D2-Elemente (Sieben-Dinge-Tabelle, Sifa-Accordion, Modul-Karten, Einmal-PDF).
// STRICT SCOPE: PREVIEW only. Preis AUSSCHLIESSLICH aus /api/catalog (kein
// Hardcoding). Trial über bestehenden Vertrag. Kein Enterprise. Chris nur
// FROZEN Production-Posen. Backend/Katalog/Checkout/Trial-Vertrag unberührt.
import { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  Loader2,
  FileText,
  Image as ImageIcon,
  Languages,
} from "lucide-react";

import AboChris from "@/components/abo/AboChris";
import AboWiedervorlageStage from "@/components/abo/AboWiedervorlageStage";
import AboLehrbilderPlakatStage from "@/components/abo/AboLehrbilderPlakatStage";
import AboPreisAbschlussStage from "@/components/abo/AboPreisAbschlussStage";
import {
  AboBrowserMockup,
  AboPhoneMockup,
  AboLaptopMockup,
} from "@/components/abo/AboMockups";
import {
  Badge,
  StoryHead,
  StoryLines,
  StorySteps,
  StoryProof,
  StoryMore,
} from "@/components/abo/AboStory";
import PlakatViewer from "@/components/abo/PlakatViewer";
import { useAboSeite } from "./useAboSeite";

import "./abo.css";

// §02 · Drei Entlastungsbotschaften (Betreiberentscheidung B2-E2 §1, wörtlich).
const ENTLASTUNG = [
  {
    title: "Nicht mehr alles vorbereiten.",
    text: "Tätigkeit nennen. CLartext bereitet vor. Sie prüfen.",
  },
  {
    title: "Nicht mehr hinterherlaufen.",
    text: "Zuweisen. Mitarbeiter erledigen. Status sehen.",
  },
  {
    title: "Nicht mehr alles im Kopf behalten.",
    text: "Fälligkeiten und Wiederholungen bleiben im Blick.",
  },
];

// §06 · Gefahrstoff-Kette (D2 §5/07 Ebene 1, code-verifiziert).
const HAZMAT_STEPS = [
  {
    title: "Gefahrstoff nennen",
    text: "„Bremsenreiniger“, „PU-Schaum“, „Benzin“. Drei Angaben: Menge, Häufigkeit, Ersatz geprüft?",
    who: "Sie",
  },
  {
    title: "Sicherheitsdatenblatt",
    text: "Automatisch gesucht oder von Ihnen hochgeladen, ausgelesen, von Ihnen gesichtet.",
    who: "CLartext + Sie",
  },
  {
    title: "Gefahrstoffverzeichnis",
    text: "Füllt sich von selbst: Einstufung, Piktogramme, Schutzausrüstung, Mengenbereich, Arbeitsbereich, SDB-Nummer. Als PDF und in der Prüfmappe.",
    who: "CLartext",
  },
  {
    title: "Haut- und Handschutzplan",
    text: "Je Arbeitsbereich per Klick aus den erfassten Gefahrstoffen erstellt – mit Plakat zum Aushängen und Prüfintervall.",
    who: "Klick Sie · Inhalt CLartext",
  },
];

const OUTFIT_HREF =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap";
const OUTFIT_MARK = "abo-outfit";

function ensureOutfitFontLink() {
  if (typeof document === "undefined") return null;
  const existing = document.querySelector(`link[data-clr-font="${OUTFIT_MARK}"]`);
  if (existing) return existing;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = OUTFIT_HREF;
  link.dataset.clrFont = OUTFIT_MARK;
  document.head.appendChild(link);
  return link;
}

const SHOT = (name) => `/abo/screenshots/${name}`;
const DEV = (name) => `/abo/devices/${name}`;
const PLAKAT = (name) => `/abo/plakate/${name}`;

export default function AboSeite() {
  const rootRef = useRef(null);
  const {
    priceState,
    currencySymbol,
    monthlyText,
    employeeCount,
    setEmployeeCount,
    totalText,
    topic,
    startTrial,
    trialLoading,
    trialError,
  } = useAboSeite();
  // Rechner-Obergrenze 50 (darüber individuelles Angebot). Minimum 1, Startwert 5.
  const setEmp = (n) => setEmployeeCount(Math.max(1, Math.min(50, n)));
  // Aktuell offener Plakat-Viewer: null | { type, src, title }
  const [viewer, setViewer] = useState(null);
  // Anker-Ziel für „CLARTEXT ansehen ↓" (erster Produktbeweis).
  const firstProofRef = useRef(null);
  const scrollToProof = () =>
    firstProofRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Outfit-Font nur für diese Seite laden + beim Unmount entfernen.
  useEffect(() => {
    const link = ensureOutfitFontLink();
    return () => {
      if (link && link.parentNode) link.parentNode.removeChild(link);
    };
  }, []);

  // Dezente Reveal-Animationen (mit Fallback ohne IntersectionObserver).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const openPlakatPdf = () =>
    setViewer({
      type: "pdf",
      src: PLAKAT("bildschirmarbeitsplaetze.pdf"),
      title: "Unterweisungsplakat · Bildschirmarbeitsplätze",
    });
  const openHautschutzPoster = () =>
    setViewer({
      type: "image",
      src: PLAKAT("hand-protection-poster.webp"),
      title: "Haut- und Handschutzplan · Plakat",
    });

  // CHRIS-MOMENT 2: bedingt mit echtem Tätigkeitsbegriff (D2 §3), sonst neutral.
  // B2-E3 §10: Chris holt den Skeptiker ab („Genau hier beginnt es.").
  const chris2Text = topic
    ? `Sagen Sie mir, worum es geht – „${topic}“ reicht. Ich bereite vor, Sie prüfen.`
    : "Sagen Sie mir, worum es geht. Ich bereite vor. Sie prüfen.";

  return (
    <div className="abo-root" data-testid="abo-page" lang="de" ref={rootRef}>
      {/* Header ------------------------------------------------------------ */}
      <header className="abo-header">
        <div className="abo-header-in">
          <span className="abo-brand" data-testid="abo-brand">
            <span className="abo-brand-mark" aria-hidden="true">
              CL
            </span>
            CLartext
          </span>
          <a
            href="/abschluss-v2"
            className="abo-header-link"
            data-testid="abo-header-back"
          >
            Zurück
          </a>
        </div>
      </header>

      <main>
        {/* ================= SCHUSS 1 · „Brauche ich das überhaupt?" ===== */}

        {/* 01 · HERO · FREIGEGEBENER BETREIBER-ENTWURF (VORLAGE, UNVERÄNDERT)
           Verbindliche Betriebsentscheidung: exakt dieses Bild IST der Hero.
           Es wird NICHT zerlegt oder verändert. Nur zwei unsichtbare, klickbare
           Flächen liegen über den gezeichneten Buttons (Trial · Ansehen) plus
           der Scroll-Pfeil. */}
        <section className="abo-heromaster-section" data-testid="abo-hero">
          <figure className="abo-heromaster">
            <img
              className="abo-heromaster-img"
              src="/abo/hero/hero-master.webp"
              width="1536"
              height="1024"
              alt="CLartext: Arbeitsschutz muss sein. Die Arbeit damit nicht. Chris zeigt CLartext am Laptop – Sie kümmern sich um Ihren Betrieb, Chris kümmert sich um die Routine."
              draggable="false"
            />
            <button
              type="button"
              className="abo-heromaster-hot abo-heromaster-hot-trial"
              data-testid="abo-trial-cta-hero"
              aria-label="14 Tage kostenlos testen"
              onClick={startTrial}
              disabled={trialLoading}
            >
              {trialLoading && (
                <span className="abo-heromaster-spin" aria-hidden="true">
                  <Loader2 size={20} className="spin" />
                </span>
              )}
            </button>
            <button
              type="button"
              className="abo-heromaster-hot abo-heromaster-hot-see"
              data-testid="abo-hero-see-cta"
              aria-label="CLartext ansehen"
              onClick={scrollToProof}
            />
            <button
              type="button"
              className="abo-heromaster-hot abo-heromaster-hot-scroll"
              data-testid="abo-hero-scroll"
              aria-label="Weiter nach unten"
              onClick={scrollToProof}
            />
          </figure>
          {/* MOBIL-CTA-02, Betreiberfreigabe 27.10.2026 — echte Knöpfe unter dem Titelbild, nur am Handy (abo.css). */}
          <div className="abo-heromobil" data-testid="abo-hero-mobil">
            <button
              type="button"
              className="abo-heromobil-trial"
              data-testid="abo-hero-mobil-trial"
              onClick={startTrial}
              disabled={trialLoading}
            >
              14 Tage kostenlos testen
            </button>
            <button
              type="button"
              className="abo-heromobil-see"
              data-testid="abo-hero-mobil-see"
              onClick={scrollToProof}
            >
              CLartext ansehen
            </button>
          </div>
          {trialError && (
            <p
              className="abo-trial-error abo-heromaster-error"
              role="alert"
              data-testid="abo-trial-error-hero"
            >
              {trialError}
            </p>
          )}
        </section>

        {/* ===================== B2-E5 · PRODUKT-BÜHNEN (Stufe 1) =====================
           Visuelle Produktgeschichte statt SaaS-Kacheln: echte Beweise groß,
           Detail-Crops, Farb-Plates, Bild vor Text, Chris = roter Faden.
           B1 GBU · B2 Unterweisung/Zuweisen · B3 Mitarbeiter-Handy + Sprache.
           Copy = eingefrorene v2 (Betreiber-Korrekturen). Hero bleibt LOCKED.
           Crops = Ausschnitte realer Screenshots (kein erfundenes UI). */}

        {/* B1 · GEFÄHRDUNGSBEURTEILUNG — helle warme Bühne, Medium rechts */}
        <section
          className="abo-stage abo-stage--light"
          data-testid="abo-stage-gbu"
          ref={firstProofRef}
          id="abo-first-proof"
          style={{ scrollMarginTop: "72px" }}
        >
          <div className="abo-stage-wrap">
            <div className="abo-devb1-grid">
              <div className="abo-devb1-copy reveal">
                <div className="abo-stage-meta">
                  <span className="abo-stage-kicker">
                    Was Chris für Sie übernimmt
                  </span>
                  <Badge kind="grund" testId="abo-gbu-badge-grund" />
                </div>
                <h2 className="abo-stage-title" data-testid="abo-gbu-title">
                  Sagen Sie nur, worum es geht. Den Rest übernimmt CLartext.
                </h2>
                <p className="abo-stage-line">
                  Tippen. Vorschlag prüfen. Freigeben.
                </p>
                <StoryMore
                  summary="Was passiert nach der Freigabe?"
                  testId="abo-gbu-more"
                >
                  <ul>
                    <li>Freigabe-Nachweis – unveränderlich abgelegt.</li>
                    <li>Welt kann Einsatzbereichen zugeordnet werden.</li>
                    <li>
                      Mitarbeiter dieser Bereiche bekommen die Unterweisung
                      automatisch.
                    </li>
                    <li>Lehrbilder werden automatisch erzeugt.</li>
                    <li>Wirksamkeitskontrolle wird als Folgetermin angelegt.</li>
                  </ul>
                </StoryMore>
              </div>
              <div className="abo-devb1-media reveal d1">
                <figure className="abo-clmon" data-testid="abo-gbu-proof">
                  <img
                    src={DEV("device-b1-gbu.webp")}
                    alt="CLartext Gefährdungsbeurteilung auf einem hochwertigen Desktop-Gerät: ein Stichwort ins Suchfeld – die passende Vorlage wird vorgeschlagen"
                    data-testid="abo-shot-gbu"
                    loading="eager"
                    draggable="false"
                  />
                </figure>
                <p className="abo-stage-cap">
                  CLartext findet die passende Gefährdungsbeurteilung und
                  bereitet sie für Sie vor.
                </p>
              </div>
              <div className="abo-devb1-chris abo-chrisgrp reveal">
                <AboChris
                  pose={23}
                  size="md"
                  testId="abo-chris-gbu"
                  text="Ich finde die passende Gefährdungsbeurteilung und bereite alles für Sie vor – Sie prüfen nur noch."
                />
              </div>
            </div>
          </div>
        </section>

        {/* B2 · UNTERWEISUNG ZUWEISEN — FROZEN STANDARD: helle Bühne, fotorealistischer
            CL-Monitor LINKS (echter assign-Screen), Chris groß RECHTS (Pose 13, Geste
            nach links zum Zuweisungs-Screen), Bubble als Figurengruppe. Gespiegelt zu
            B1 (alterniert → keine Monitor-Parade). Screenshot echt/unverändert. */}
        <section
          className="abo-stage abo-stage--b2"
          data-testid="abo-stage-unterweisung"
        >
          <div className="abo-stage-wrap">
            <div className="abo-devb2-grid">
              <div className="abo-devb2-copy reveal">
                <div className="abo-stage-meta">
                  <span className="abo-stage-kicker">Unterweisung</span>
                  <Badge kind="grund" testId="abo-unterweisung-badge-grund" />
                </div>
                <h2
                  className="abo-stage-title"
                  data-testid="abo-unterweisung-title"
                >
                  Unterweisung? Die kommt zu Ihren Mitarbeitern.
                </h2>
                <p className="abo-stage-line">
                  Empfänger wählen, Frist setzen – fertig. Kein Stuhlkreis.
                </p>
                <StoryMore
                  summary="Gilt das rechtlich?"
                  testId="abo-unterweisung-more"
                >
                  <p>
                    Elektronische Unterweisung ist zulässig, wenn die Inhalte
                    arbeitsplatzbezogen sind, das Verständnis geprüft wird und
                    Rückfragen möglich bleiben (DGUV Regel 100-001 zu § 4 DGUV
                    Vorschrift 1). CLartext liefert Inhalte, Verständnisfragen und
                    Nachweis – Rückfragen und praktische Anteile bleiben bei Ihnen.
                  </p>
                </StoryMore>
              </div>
              <div className="abo-devb2-media reveal d1">
                <figure className="abo-clmon" data-testid="abo-unterweisung-proof">
                  <img
                    src={DEV("device-b2-assign.webp")}
                    alt="CLartext: Unterweisung zuweisen auf einem hochwertigen Desktop-Gerät – Empfänger und Einsatzbereiche wählen, Mitarbeiter automatisch vorausgewählt"
                    data-testid="abo-shot-assign"
                    loading="eager"
                    draggable="false"
                  />
                </figure>
              </div>
              <div className="abo-devb2-chris abo-chrisgrp reveal">
                <AboChris
                  pose={13}
                  size="md"
                  testId="abo-chris-unterweisung"
                  text="Ich bringe die Unterweisung zu Ihren Mitarbeitern. Sie müssen niemandem hinterherlaufen."
                />
              </div>
            </div>
          </div>
        </section>

        {/* B3 · MITARBEITERZUGANG — Betreiber-approved (29.09.2026), 1:1 aus
            /dev/abo-b3 übernommen. Frozen Standard: fotorealistischer CL-Monitor
            (echter Computer-Screenshot device-b3-computer) + überlappendes iPhone
            (device-handy) = zwei echte Zugangswege, gleicher Inhalt; Chris groß
            (Pose 17) zeigt zu den Geräten; sehr helle CI, Sprachen sichtbar.
            Eigene Mobile-Komposition. Aussage „Ein Inhalt. Zwei Wege." */}
        <section className="b3-stage" data-testid="abo-stage-handy">
          <div className="b3-wrap">
            <header className="b3-head reveal">
              <div className="b3-meta">
                <span className="b3-kicker">Mitarbeiter-Zugang</span>
                <span className="b3-badge" data-testid="abo-handy-badge-grund">
                  Grundabo
                </span>
              </div>
              <h2 className="b3-title" data-testid="abo-handy-title">
                Unterweisung dort, wo es für den Mitarbeiter passt.
              </h2>
              <p className="b3-sub">Am Handy. Oder am Computer.</p>
              <span className="b3-tag" data-testid="abo-handy-tag">
                Ein Inhalt. Zwei Wege.
              </span>
            </header>

            <div className="b3-grid">
              {/* Gerätegruppe · iPhone + CL-Monitor = zwei echte Zugangswege, gleicher Inhalt */}
              <div className="b3-devgroup reveal d1" data-testid="abo-handy-proof">
                <figure className="b3-phone">
                  <img
                    src={SHOT("device-handy.webp")}
                    alt="CLartext: Mitarbeiter bearbeitet die Unterweisung am eigenen Handy – mit Lehrbildern (Gefahrenmarkierungen psychisch/mechanisch)"
                    data-testid="abo-shot-unterweisung-handy"
                    loading="eager"
                    draggable="false"
                  />
                </figure>
                <figure className="abo-clmon b3-mon">
                  <img
                    src={DEV("device-b3-computer.webp")}
                    alt="CLartext auf einem hochwertigen Desktop-Gerät: derselbe Unterweisungsinhalt am Computer – mit Lehrbildern (Gefahrenmarkierungen psychisch/mechanisch)"
                    data-testid="abo-shot-unterweisung-computer"
                    loading="eager"
                    draggable="false"
                  />
                </figure>
              </div>

              {/* Chris groß & richtungsrichtig (Pose 17 zeigt nach links zu den Geräten) + Sprachen */}
              <div
                className="b3-chris abo-chrisgrp reveal d2"
                data-testid="abo-chris-handy-wrap"
              >
                <AboChris
                  pose={17}
                  size="md"
                  testId="abo-chris-handy"
                  text="Ich bringe die Unterweisung zu Ihren Mitarbeitern – aufs Handy oder an den Computer. Und auf Wunsch in der Muttersprache Ihrer Mitarbeiter."
                />
                <div className="b3-lang" data-testid="abo-handy-sprache">
                  <p className="b3-lang-head">
                    <Languages size={17} aria-hidden="true" />
                    <span>In der Sprache, die Ihr Mitarbeiter versteht:</span>
                  </p>
                  <ul className="b3-flags">
                    <li><span className="fl" aria-hidden="true">🇩🇪</span>Deutsch</li>
                    <li><span className="fl" aria-hidden="true">🇬🇧</span>English</li>
                    <li><span className="fl" aria-hidden="true">🇵🇱</span>Polski</li>
                    <li><span className="fl" aria-hidden="true">🇷🇴</span>Română</li>
                    <li><span className="fl" aria-hidden="true">🇹🇷</span>Türkçe</li>
                    <li><span className="fl" aria-hidden="true">🇺🇦</span>Українська</li>
                    <li><span className="fl" aria-hidden="true">🇧🇬</span>Български</li>
                    <li><span className="fl" aria-hidden="true">🇭🇷</span>Hrvatski</li>
                    <li><span className="fl" aria-hidden="true">🇷🇺</span>Русский</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* B4 · WIEDERVORLAGE / WIEDERHOLUNG / AMPEL — echter Owner-Dashboard-Beweis
            (Crop B „Entlastung"). Ausgelagert in Komponente (auch als Dev-Vorschau
            isoliert prüfbar). Copy/Screenshot/Crop/Chris/Komposition = Betreiber-Freigabe. */}
        <AboWiedervorlageStage />

        {/* B5 · LEHRBILDER + QR-PLAKAT — NACH der Wiedervorlage/Ampel (Betreiber-
            Dramaturgie: Automatisierungs-Kernprozess Zuweisen→Erledigen→Wiederholung
            NICHT unterbrechen; das Plakat kommt als sichtbares Extra am Ende des
            Unterweisungsblocks). „Plakat ansehen" öffnet die VOLLSTÄNDIGE echte PDF
            (pdf.js, alle Seiten scrollbar). Copy = Arbeitsstand (nicht eingefroren). */}
        <AboLehrbilderPlakatStage onViewPlakat={openPlakatPdf} />

        {/* 06 · GEFAHRSTOFF-BÜHNE — Betreiber-approved, 1:1 aus /dev/abo-gefahrstoff
            übernommen (29.09.2026). Fotorealistischer CL-Monitor + echte
            Sterilium-Screenshots + Chris (Pose 17/23) im Frozen Standard.
            3 Szenen: Suche → was entsteht (Lehrbilder/PDF) → Verzeichnis + SDB.
            KEIN Handschutzplan (bewusst weggelassen). NICHTS am Rollout geändert. */}
        <section className="gh-stage" data-testid="abo-gefahrstoff">
          <div className="gh-wrap">
            <header className="gh-head reveal">
              <div className="gh-meta">
                <span className="gh-kicker">Gefahrstoffe</span>
                <span className="gh-tag" data-testid="gh-zusatz-tag">
                  Zusatzmodul
                </span>
              </div>
              <h2 className="gh-title" data-testid="gh-title">
                Ein Gefahrstoff. Die komplette Dokumentation.
              </h2>
              <p className="gh-line">
                Sie tippen den Namen. Den Rest übernimmt CLartext.
              </p>
            </header>

            {/* Szene 1 · Eingabe „Sterilium" + Chris (Kette, Pose 17) — Monitor links, Chris rechts */}
            <div
              className="abo-devb4-grid gh-scene reveal"
              data-testid="gh-scene-suche"
            >
              <figure
                className="abo-clmon abo-devb4-monitor"
                data-testid="gh-search-proof"
              >
                <img
                  src={DEV("device-gh-search.webp")}
                  alt="CLartext auf einem hochwertigen Desktop-Gerät: Sterilium eingetippt – 1 Treffer, Sterillium Händedesinfektionsmittel als Dokumentation"
                  data-testid="gh-shot-search"
                  loading="lazy"
                  draggable="false"
                />
              </figure>
              <div
                className="abo-devb4-chris abo-chrisgrp"
                data-testid="gh-chris1-wrap"
              >
                <AboChris
                  pose={17}
                  size="md"
                  testId="gh-chris-1"
                  text="Sie nennen mir den Gefahrstoff. Ich bereite die Unterweisung vor, mache die Inhalte verständlich, erstelle Ihr Gefahrstoffverzeichnis und halte es aktuell."
                />
              </div>
            </div>

            {/* Szene 2 · Was daraus entsteht — Detailbeweise (kein Chris) */}
            <div className="gh-scene reveal" data-testid="gh-scene-inhalte">
              <p className="gh-eyebrow">Was daraus entsteht</p>
              <div className="gh-proofs">
                <figure className="gh-proof" data-testid="gh-proof-lehrbilder">
                  <img
                    src={SHOT("gh-lehrbilder-sterilium.webp")}
                    alt="CLartext Sterillium-Unterweisung: Sicherheitssymbole und Richtig/Falsch-Lehrbilder zum Umgang mit dem Händedesinfektionsmittel"
                    data-testid="gh-shot-lehrbilder"
                    loading="lazy"
                    draggable="false"
                  />
                  <figcaption>
                    Verständliche Lehrbilder &amp; Sicherheitszeichen
                    <span>
                      Aus dem Produkt automatisch erzeugt – nicht abstrakt,
                      sondern gezeigt.
                    </span>
                  </figcaption>
                </figure>
                <figure className="gh-proof" data-testid="gh-proof-pdf">
                  <img
                    src={SHOT("gh-verzeichnis-pdf-sterilium.webp")}
                    alt="CLartext Gefahrstoffverzeichnis als PDF nach § 6 Abs. 12 GefStoffV mit Sterillium classic pure (BODE Chemie), Spalten Bezeichnung, Hersteller, PSA, Mengenbereich, Arbeitsbereich, SDB-Belegnummer"
                    data-testid="gh-shot-pdf"
                    loading="lazy"
                    draggable="false"
                  />
                  <figcaption>
                    Gefahrstoffverzeichnis als PDF
                    <span>
                      Prüffertig nach § 6 Abs. 12 GefStoffV – jederzeit
                      exportierbar.
                    </span>
                  </figcaption>
                </figure>
              </div>
            </div>

            {/* Szene 3 · Verzeichnis lebt + SDB + Chris (Pose 23) — Monitor rechts, Chris links */}
            <div
              className="abo-devb1-grid gh-scene reveal"
              data-testid="gh-scene-verzeichnis"
            >
              <div className="abo-devb1-copy" data-testid="gh-verz-copy">
                <p className="gh-eyebrow">Verzeichnis &amp; Sicherheitsdatenblatt</p>
                <h3 className="gh-cap-head">
                  Automatisch gepflegt. Beleg inklusive.
                </h3>
                <p className="gh-cap-line">
                  Aus Ihren erfassten Gefahrstoffen erstellt CLartext automatisch
                  das Gefahrstoffverzeichnis – übersichtlich dokumentiert und mit
                  dem zugehörigen Sicherheitsdatenblatt verknüpft.
                </p>
              </div>
              <div className="abo-devb1-media" data-testid="gh-register-proof">
                <figure className="abo-clmon" style={{ margin: 0 }}>
                  <img
                    src={DEV("device-gh-register.webp")}
                    alt="CLartext Gefahrstoffverzeichnis-UI auf einem hochwertigen Desktop-Gerät: Verzeichnis als PDF, Suche, Filter nach Einsatzbereich, Detail mit Substitutionsprüfung"
                    data-testid="gh-shot-register"
                    loading="lazy"
                    draggable="false"
                  />
                </figure>
              </div>
              <div
                className="abo-devb1-chris abo-chrisgrp"
                data-testid="gh-chris2-wrap"
              >
                <AboChris
                  pose={23}
                  size="md"
                  testId="gh-chris-2"
                  text="Das Sicherheitsdatenblatt suche ich selbstständig für Sie. Das Original speichere ich mit ab – für Ihre Dokumentation."
                />
              </div>
            </div>
          </div>
        </section>

        {/* 07 · FÜHRERSCHEINKONTROLLE — Betreiber-approved (30.09.2026), 1:1 aus
            /dev/abo-fuehrerschein übernommen. Fotorealistisches Hauptmotiv
            (fuehrerschein-hauptmotiv.webp) als EIN Asset, groß & präsent (kein
            Monitor, keine kleine Karte); Headline + Subline oberhalb; dezente
            ZUSATZMODUL-Kennzeichnung; grüne NEU-Eck-Banderolen links + rechts
            (kein Verlauf, keine Animation). Bild-Inhalt 1:1 LOCKED. */}
        <section className="fs-stage" data-testid="abo-fuehrerschein">
          <div className="fs-wrap">
            <div className="fs-marks reveal">
              <span className="fs-zusatz" data-testid="abo-fuehrerschein-zusatz">
                Zusatzmodul
              </span>
            </div>
            <header className="fs-head reveal">
              <h2 className="fs-title" data-testid="abo-fuehrerschein-title">
                Führerscheinkontrolle ohne Listen, Ordner und Hinterherlaufen.
              </h2>
              <p className="fs-sub">
                Mitarbeiter fotografiert. Sie prüfen. CLartext erinnert automatisch
                an die nächste Kontrolle.
              </p>
            </header>
            <figure className="fs-hero reveal d1" data-testid="abo-fuehrerschein-hero">
              <img
                src={SHOT("fuehrerschein-hauptmotiv.webp")}
                alt="CLartext Führerscheinkontrolle: Mitarbeiter fotografiert Vorder- und Rückseite des Führerscheins im Handy-Zugang, der Unternehmer prüft und gibt frei, CLartext dokumentiert und übernimmt die automatische Wiedervorlage."
                data-testid="abo-shot-driver-check"
                width="1536"
                height="1024"
                loading="lazy"
                draggable="false"
              />
              <span className="fs-ribbon-wrap fs-ribbon-wrap--left" aria-hidden="true">
                <span className="fs-ribbon fs-ribbon--left">Neu</span>
              </span>
              <span className="fs-ribbon-wrap fs-ribbon-wrap--right" aria-hidden="true">
                <span className="fs-ribbon fs-ribbon--right">Neu</span>
              </span>
            </figure>
          </div>
        </section>

        {/* 08 · PRÜFMAPPE · ABSCHLUSS SCHUSS 2 ----------------------------- */}
        {/* Live-Rollout 30.09.2026 (1:1 aus /dev/abo-pruefmappe, Operator-GO):
            Frozen Visual Standard — CL-Mac (reale BG-Prüfpaket-Struktur 00–08
            inkl. 08 Führerscheinkontrollen, Backend-Gap geschlossen) + Chris
            groß (Pose 17) zeigt zum Mac. Ersetzt den alten abstrakten
            `abo-mappe`-Ordnerbaum. Keine Technikwerbung (WORM/SHA-256). */}
        <section className="pm-stage" data-testid="abo-pruefmappe">
          <div className="pm-wrap">
            <header className="pm-head reveal">
              <div className="pm-meta">
                <span className="pm-kicker">Prüfmappe</span>
                <span className="pm-badge" data-testid="abo-pruefmappe-badge">
                  Grundabo
                </span>
              </div>
              <h2 className="pm-title" data-testid="abo-pruefmappe-head">
                Berufsgenossenschaft vor der Tür? Ihre Nachweise sind schon bereit.
              </h2>
              <p className="pm-sub">
                Prüfmappe herunterladen statt Unterlagen zusammensuchen.
              </p>
              <p className="pm-note">Alle Nachweise. Eine Prüfmappe. Ein Download.</p>
            </header>

            {/* CL-Mac (echte BG-Prüfpaket-Struktur) links · Chris rechts, Pose 17 zeigt zum Mac */}
            <div
              className="abo-devb4-grid gh-scene reveal"
              data-testid="abo-pruefmappe-scene"
            >
              <figure
                className="abo-clmon abo-devb4-monitor"
                data-testid="abo-pruefmappe-mac"
              >
                <img
                  src={DEV("device-pruefmappe.webp")}
                  alt="CLartext Prüfmappe auf einem hochwertigen Desktop-Gerät: BG-Prüfpaket als ein Download mit Deckblatt, Nachweisliste, Freigaben, Unterweisungen, Prüfprotokoll, Wirksamkeitskontrollen, Gefahrstoffen, Handschutzplänen und Führerscheinkontrollen."
                  data-testid="abo-pruefmappe-shot"
                  loading="lazy"
                  draggable="false"
                />
              </figure>
              <div className="abo-devb4-chris abo-chrisgrp" data-testid="abo-pruefmappe-chris-wrap">
                <AboChris
                  pose={17}
                  size="md"
                  testId="abo-chris-pruefmappe"
                  text="Die Berufsgenossenschaft möchte Ihre Nachweise sehen? Sie suchen nicht. Sie laden die Prüfmappe herunter."
                />
              </div>
            </div>
          </div>
        </section>

        {/* 09 · PREIS-/ABSCHLUSSBÜHNE (freigegeben 30.09.2026) — Master-Bild 1:1
           + unsichtbare Technik. Ersetzt vollständig die alte Preis- (§09) und
           Abschluss-Darstellung (§10). Preis dynamisch aus /api/catalog, nichts
           hartcodiert; Trial-CTA = bestehender startTrial-Flow; PDF-Hotspot = echte
           Leistungsübersicht. Details: components/abo/AboPreisAbschlussStage.jsx. */}
        <AboPreisAbschlussStage
          priceState={priceState}
          currencySymbol={currencySymbol}
          monthlyText={monthlyText}
          employeeCount={employeeCount}
          setEmp={setEmp}
          totalText={totalText}
          startTrial={startTrial}
          trialLoading={trialLoading}
          trialError={trialError}
        />
      </main>

      <footer className="abo-footer" data-testid="abo-footer">
        CLartext · Arbeitsschutz-Dokumentation
        {/* ABO-RECHT-01, Betreiberfreigabe 24.10.2026: Rechtslinks wie Startseite (landing/index.html:286-289). */}
        <nav aria-label="Rechtliches" data-testid="abo-legal-nav" className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
          <a href="/impressum" data-testid="abo-legal-impressum">Impressum</a>
          <a href="/datenschutz" data-testid="abo-legal-datenschutz">Datenschutz</a>
          <a href="/agb" data-testid="abo-legal-agb">AGB</a>
          <a href="/avv" data-testid="abo-legal-avv">Auftragsverarbeitung</a>
        </nav>
      </footer>

      <PlakatViewer
        open={!!viewer}
        onClose={() => setViewer(null)}
        type={viewer?.type}
        src={viewer?.src}
        title={viewer?.title}
        testId="abo-plakat-viewer"
      />
    </div>
  );
}
